// ============================================================
// EFFECT SCOPE (v8.367.4) - who a weapon effect reaches.
//
// A blast damages everyone it catches (the 2026-08-17 ruling stands);
// what it does BESIDE the damage - an affliction, an exposure, a
// hallucinogen - may be meant for the enemy only, or for your own
// squad only (a medicae dart, a litany). Teams are STRICT: a shared
// non-blank SIDE is an ally, a different one a hostile, and somebody
// with no team is neither - only "anyone" reaches them. Callers pass
// `sideKey(side, tag)` (2026-10-23: allied teams share a side).
//
// Pure, and zero React dependency.
// ============================================================

export type EffectAffects = 'anyone' | 'hostiles' | 'allies';

const DEFAULT_EFFECT_AFFECTS: EffectAffects = 'anyone';

export interface EffectAffectsEntry {
  key: EffectAffects;
  label: string;
  hint: string;
}

export const EFFECT_AFFECTS: EffectAffectsEntry[] = [
  { key: 'anyone', label: 'Anyone it hits', hint: 'Friend, foe or nobody’s - whoever the hit lands on.' },
  {
    key: 'hostiles',
    label: 'Hostiles only',
    hint: 'A victim on a DIFFERENT team than the attacker. A teamless victim is nobody’s hostile.',
  },
  {
    key: 'allies',
    label: 'Allies only',
    hint: 'A victim on the attacker’s OWN team - a buff delivered by a hit. A teamless victim is nobody’s ally.',
  },
];

export function isEffectAffects(value: unknown): value is EffectAffects {
  return value === 'anyone' || value === 'hostiles' || value === 'allies';
}

const tag = (t: string | null | undefined) => (t && t.trim() ? t.trim() : null);

/**
 * The key ally and hostile compare (2026-10-23): a combatant's SIDE when it carries one, else its team tag - so an
 * old payload with no side behaves exactly as before. `engine/roster/team.ts` `sideOf` is where the side comes from.
 */
export function sideKey(side: string | null | undefined, teamTag: string | null | undefined): string | null {
  return tag(side) ?? tag(teamTag);
}

/** Does an effect marked `affects` reach this victim, given both sides (`sideKey`)? */
export function effectReaches(
  affects: EffectAffects | undefined,
  attackerTeamTag: string | null | undefined,
  victimTeamTag: string | null | undefined,
): boolean {
  const a = tag(attackerTeamTag);
  const v = tag(victimTeamTag);
  switch (affects ?? DEFAULT_EFFECT_AFFECTS) {
    case 'anyone':
      return true;
    case 'hostiles':
      return !!a && !!v && a !== v;
    case 'allies':
      return !!a && !!v && a === v;
  }
}
